import {lazy, Suspense, useContext, useState} from 'react'

import PropTypes from 'prop-types'

import {atomButtonColors} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import useEventListener from '@s-ui/react-hooks/lib/useEventListener'
import {useRouter} from '@s-ui/react-router'
import Layout from '@adv-mt/mt-layout-basic'
import StepperFlow from '@adv-mt/mt-stepper-flow'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../components/LinkFactory/index.js'
import {BodyTypesFilter} from './BodyTypes/body-types-filter.js'
import {ExitButton} from './components/Buttons/exit-button.js'
import {Title} from './components/Title/index.js'
import {FuelTypesFilter} from './FuelTypes/fuel-types-filter.js'
import {useSuggesterTracking} from './hooks/useSuggesterTracking.js'
import {useTags} from './hooks/useTags.js'
import {MakesFilter} from './Makes/makes-filter.js'
import {PriceRangeFilter} from './PriceRange/price-range-filter.js'
import {SeatsFilter} from './Seats/seats-filter.js'
import {getInitialProps} from './getInitialProps.js'
import {BASE_CLASS, defaultTopbarProps, KEYBOARD_EVENTS, PRICE_MODE, TOTAL_STEPS} from './settings.js'
import {getMakesLabels} from './utils.js'

const NotificationsStepper = lazy(() =>
  import(/* webpackChunkName: "NotificationsStepper" */ '@adv-mt/mt-notifications-stepper')
)

const VehicleSuggester = ({
  __HTTP__ = {},
  description,
  h1,
  image,
  isError,
  isLoading,
  isPriceModeEnabled,
  loadThirdParties,
  nextPagePath,
  priceRangeOptions: priceRangeOptionsProp = [],
  bodyTypeOptions = [],
  fuelTypeOptions = [],
  seatsOptions = [],
  makeOptions = [],
  pageName,
  previousPagePath,
  step,
  stepBreadcrumb,
  title
}) => {
  const {redirectTo} = __HTTP__

  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')

  const {
    VEHICLE_CONFIGURATOR_PATH: exitPath,
    VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME,
    VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME,
    VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME,
    VEHICLE_SUGGESTER_SEATS_PAGE_NAME,
    VEHICLE_SUGGESTER_MAKES_PAGE_NAME
  } = config.get('page')
  const {VEHICLE_SUGGESTER_TRACKING_ACTIONS: actions} = config.get('tracking')

  const isPriceRangePage = pageName === VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME
  const isBodyTypePage = pageName === VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME
  const isFuelTypePage = pageName === VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME
  const isSeatsPage = pageName === VEHICLE_SUGGESTER_SEATS_PAGE_NAME
  const isMakesPage = pageName === VEHICLE_SUGGESTER_MAKES_PAGE_NAME

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [selectedPriceRange, setSelectedPriceRange] = useState()
  const [selectedBodyTypes, setSelectedBodyTypes] = useState()
  const [selectedFuelTypes, setSelectedFuelTypes] = useState()
  const [selectedSeats, setSelectedSeats] = useState()
  const [selectedMakes, setSelectedMakes] = useState()
  const [errorMessage, setErrorMessage] = useState(null)
  const router = useRouter()

  // All six suggester steps share this same mounted component instance (RR7 only swaps
  // `useLoaderData`), and the Results step swaps in a different component type, which forces a
  // remount. Seeding state from the props of whatever step the instance was born on locked the
  // price options into `[]` when that step was not the price range one. So the loader data is
  // read on every render, and state only holds what the user changed on the price range step
  // (the price mode and the options fetched for it), which then wins over the loader data.
  const [priceModeOverride, setPriceModeOverride] = useState(null)
  const [priceRangeOptionsOverride, setPriceRangeOptionsOverride] = useState(null)
  const selectedPriceMode = priceModeOverride ?? (isPriceModeEnabled ? PRICE_MODE.INSTALLMENT : PRICE_MODE.FULL_PRICE)
  const priceRangeOptions = priceRangeOptionsOverride ?? priceRangeOptionsProp

  const makesLabels = getMakesLabels({selectedMakes, makes: isMakesPage ? makeOptions : []})
  const tags = useTags({
    priceRange: selectedPriceRange,
    priceMode: selectedPriceMode,
    bodyTypeIds: selectedBodyTypes,
    fuelTypeIds: selectedFuelTypes,
    seats: selectedSeats,
    makes: makesLabels
  })
  const {trackAction} = useSuggesterTracking()

  useEventListener('keydown', event => {
    if (event.key === KEYBOARD_EVENTS.ENTER || event.key === KEYBOARD_EVENTS.ARROW_RIGHT) {
      _handleNext()
    }
    if (event.key === KEYBOARD_EVENTS.BACKSPACE || event.key === KEYBOARD_EVENTS.ARROW_LEFT) {
      _handleBack()
    }
  })

  useEventListener('beforeunload', event => {
    event.preventDefault()
    event.returnValue = ''
  })

  const topbarProps = {
    ...defaultTopbarProps,
    navButton: <ExitButton onClick={() => router.push(exitPath)} />
  }

  if (redirectTo) {
    router.push(redirectTo)
    return <></>
  }

  if (isError) {
    return (
      <main>
        <Layout.FullWidth linkFactory={LinkFactory} footerProps={{isSimple: true}} topbarProps={topbarProps}>
          <Layout.Container isFullWidth isFullHeight>
            <StepperFlow.Error
              title={title}
              description={description}
              image={image}
              onBack={() => router.goBack()}
              onExit={() => router.push(exitPath)}
            />
          </Layout.Container>
        </Layout.FullWidth>
      </main>
    )
  }

  const ctaWrapperClass = `${BASE_CLASS}-ctaWrapper sticky`
  const formData = {
    priceRange: selectedPriceRange?.value,
    bodyTypeIds: selectedBodyTypes,
    fuelTypeIds: selectedFuelTypes,
    seats: selectedSeats?.value,
    makes: selectedMakes
  }

  const getSelectedOption = () => {
    if (isPriceRangePage) return `${selectedPriceRange.value.from}-${selectedPriceRange.value.to}`
    if (isBodyTypePage) return selectedBodyTypes.join(',')
    if (isFuelTypePage) return selectedFuelTypes.join(',')
    if (isSeatsPage) return selectedSeats?.value.join(',')
    if (isMakesPage) return selectedMakes?.join(',')
    return null
  }

  const validateCurrentPage = () => {
    let hasError = false
    if (isPriceRangePage && !selectedPriceRange) {
      setErrorMessage(i18n.t('VEHICLE_SUGGESTER.STEP_PRICE_RANGE.VALIDATION_ERROR_MESSAGE'))
      hasError = true
    }
    if (isBodyTypePage && !selectedBodyTypes?.length) {
      setErrorMessage(i18n.t('VEHICLE_SUGGESTER.STEP_BODY_TYPES.VALIDATION_ERROR_MESSAGE'))
      hasError = true
    }
    if (isFuelTypePage && !selectedFuelTypes?.length) {
      setErrorMessage(i18n.t('VEHICLE_SUGGESTER.STEP_FUEL_TYPES.VALIDATION_ERROR_MESSAGE'))
      hasError = true
    }
    if (isSeatsPage && !selectedSeats) {
      setErrorMessage(i18n.t('VEHICLE_SUGGESTER.STEP_SEATS.VALIDATION_ERROR_MESSAGE'))
      hasError = true
    }
    return hasError
  }

  const _handleNext = () => {
    const hasError = validateCurrentPage()
    if (hasError) {
      trackAction({pageName, action: actions.NEXT_BUTTON_CLICKED_ERROR})
      return
    }
    trackAction({pageName, action: actions.NEXT_BUTTON_CLICKED, selectedOption: getSelectedOption()})
    router.push({pathname: nextPagePath, state: formData})
  }

  const _handleOnSubmit = async () => {
    const hasError = validateCurrentPage()
    if (hasError) return
    trackAction({
      pageName,
      action: actions.NEXT_BUTTON_CLICKED,
      selectedOption: getSelectedOption()
    })
    try {
      setIsLoadingSubmit(true)
      router.push({pathname: nextPagePath, state: formData})
    } catch (error) {
      setIsLoadingSubmit(false)
      console.error('Error fetching vehicle suggestions: ', error)
    }
  }

  const _handlePriceModeChange = async newPriceMode => {
    setPriceModeOverride(newPriceMode)
    setSelectedPriceRange(undefined)
    const [error, priceRangeOptionsResponse] = await domain
      .get('get_configurator_price_range_options_vehicles_use_case')
      .execute()
    if (error) return
    const newOptions =
      newPriceMode === PRICE_MODE.INSTALLMENT
        ? priceRangeOptionsResponse.getFilterOptionsWithFees()
        : priceRangeOptionsResponse.getFilterOptions()
    setPriceRangeOptionsOverride(newOptions)
  }

  const _handleBack = () => {
    trackAction({pageName, action: actions.BACK_BUTTON_CLICKED})
    if (isBodyTypePage) setSelectedBodyTypes(null)
    if (isFuelTypePage) setSelectedFuelTypes(null)
    if (isSeatsPage) setSelectedSeats(null)
    if (isMakesPage) setSelectedMakes(null)
    previousPagePath ? router.push({pathname: previousPagePath, state: formData}) : router.goBack()
  }

  return (
    <>
      <Layout.FullWidth linkFactory={LinkFactory} footerProps={{isSimple: true}} topbarProps={topbarProps}>
        <Layout.Container isFullWidth isFullHeight>
          <main className={`${BASE_CLASS}`}>
            <section className={`${BASE_CLASS}-titleWrapper`}>
              <Title title={h1} />
            </section>
            <section className={`${BASE_CLASS}-stepperWrapper`}>
              <StepperFlow>
                <StepperFlow.ProgressBar breadcrumb={stepBreadcrumb} step={step} totalSteps={TOTAL_STEPS} />
                <StepperFlow.StepTags tags={tags} />
                <StepperFlow.StepTitle title={title} description={description} />
                <StepperFlow.Step isLoading={isLoading || isLoadingSubmit}>
                  <div className={`${BASE_CLASS}-content`}>
                    {isPriceRangePage && (
                      <PriceRangeFilter
                        key={selectedPriceMode}
                        isPriceModeEnabled={isPriceModeEnabled}
                        options={priceRangeOptions}
                        onChange={setSelectedPriceRange}
                        value={selectedPriceRange}
                        onPriceModeChange={_handlePriceModeChange}
                        priceMode={selectedPriceMode}
                      />
                    )}
                    {isBodyTypePage && (
                      <BodyTypesFilter
                        options={bodyTypeOptions}
                        onChange={setSelectedBodyTypes}
                        value={selectedBodyTypes}
                      />
                    )}
                    {isFuelTypePage && (
                      <FuelTypesFilter
                        options={fuelTypeOptions}
                        onChange={setSelectedFuelTypes}
                        value={selectedFuelTypes}
                      />
                    )}
                    {isSeatsPage && (
                      <SeatsFilter options={seatsOptions} onChange={setSelectedSeats} value={selectedSeats} />
                    )}
                    {isMakesPage && (
                      <MakesFilter options={makeOptions} onChange={setSelectedMakes} value={selectedMakes} />
                    )}
                    {loadThirdParties && <TrackingPageView pageName={pageName} />}
                  </div>
                </StepperFlow.Step>
              </StepperFlow>
            </section>
            <section className={ctaWrapperClass}>
              <div className={`${BASE_CLASS}-content ${BASE_CLASS}-buttonGroup`}>
                <StepperFlow.BackButton className={`${BASE_CLASS}-button`} onClick={_handleBack}>
                  {i18n.t('NAVIGATION_PAGINATION_PREVIOUS')}
                </StepperFlow.BackButton>
                {step < TOTAL_STEPS && (
                  <StepperFlow.NextButton
                    isLoading={isLoading || isLoadingSubmit}
                    className={`${BASE_CLASS}-button`}
                    onClick={_handleNext}
                  >
                    {i18n.t('NAVIGATION_PAGINATION_NEXT')}
                  </StepperFlow.NextButton>
                )}
                {step === TOTAL_STEPS && (
                  <StepperFlow.NextButton
                    isLoading={isLoading || isLoadingSubmit}
                    color={atomButtonColors.ACCENT}
                    className={`${BASE_CLASS}-button`}
                    onClick={_handleOnSubmit}
                  >
                    {i18n.t('COMMON_SHOW_RESULTS')}
                  </StepperFlow.NextButton>
                )}
              </div>
            </section>
          </main>
        </Layout.Container>
      </Layout.FullWidth>
      {errorMessage && (
        <Suspense fallback={<></>}>
          <NotificationsStepper type="error" message={errorMessage} onClose={() => setErrorMessage(null)} />
        </Suspense>
      )}
    </>
  )
}

VehicleSuggester.displayName = 'VehicleSuggester'
VehicleSuggester.getInitialProps = getInitialProps
VehicleSuggester.propTypes = {
  __HTTP__: PropTypes.shape({
    redirectTo: PropTypes.string
  }),
  description: PropTypes.string,
  h1: PropTypes.string,
  image: PropTypes.string,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isPriceModeEnabled: PropTypes.bool,
  loadThirdParties: PropTypes.bool,
  nextPagePath: PropTypes.string,
  priceRangeOptions: PropTypes.array,
  bodyTypeOptions: PropTypes.array,
  fuelTypeOptions: PropTypes.array,
  seatsOptions: PropTypes.array,
  makeOptions: PropTypes.array,
  pageName: PropTypes.string,
  previousPagePath: PropTypes.string,
  step: PropTypes.number,
  stepBreadcrumb: PropTypes.string,
  title: PropTypes.string
}

VehicleSuggester.keepMounted = true

export default VehicleSuggester
